import React, { useMemo, useState } from "react";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import { getSettlementLedgerDescriptions } from "./settlementLEdgerDescriptions";

const formatCurrency = (value) => `${Number(value ?? 0).toFixed(2)} zł`;

const SettlementVariantReturnsCard = ({ variant }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedInfoKey, setSelectedInfoKey] = useState(null);
  const [showFundInfo, setShowFundInfo] = useState(false);

  const rows = useMemo(() => {
    return (variant.reservations ?? [])
      .map((reservation) => {
        const participants = reservation.hasFullReservationRefund
          ? (reservation.participants ?? []).map((p) => {
              const excludedNnwAmount = Number(p.nnwExcludedFromFullRefundAmount || 0);
              return { ...p, effectiveFullRefundAmount: Number(p.fullRefundAmount || 0), isCoveredByFullRefund: true, hasExcludedNnwInFullRefund: excludedNnwAmount > 0, excludedNnwAmount };
            })
          : (reservation.participants ?? []).map((p) => {
              const effectiveFullRefundAmount = Number(p.fullRefundAmount || 0);
              const isCoveredByFullRefund = effectiveFullRefundAmount > 0;
              const excludedNnwAmount = Number(p.nnwExcludedFromFullRefundAmount || 0);
              return { ...p, effectiveFullRefundAmount, isCoveredByFullRefund, hasExcludedNnwInFullRefund: isCoveredByFullRefund && excludedNnwAmount > 0, excludedNnwAmount };
            });

        const equalShareTotal = participants.reduce((sum, p) => sum + Number(p.equalShareAmount || 0), 0);
        const participantAdjustmentsTotal = participants.reduce((sum, p) => sum + Number(p.participantAdjustmentAmount || 0), 0);
        const manualAdjustmentsImpactTotal = participants.reduce((sum, p) => sum + Math.abs(Number(p.participantAdjustmentAmount || 0)), 0);
        const cancelledTfgReturnsTotal = participants.reduce((sum, p) => sum + Number(p.cancelledTfgReturnAmount || 0), 0);
        const unusedNnwReturnsTotal = participants.reduce((sum, p) => sum + Number(p.unusedNnwReturnAmount || 0), 0);
        const unusedKrReturnsTotal = participants.reduce((sum, p) => sum + Number(p.unusedKrReturnAmount || 0), 0);
        const unusedInsuranceReturnsTotal = participants.reduce((sum, p) => sum + Number(p.unusedInsuranceReturnAmount || 0), 0);
        const fullParticipantRefundsTotal = participants.reduce((sum, p) => sum + Number(p.effectiveFullRefundAmount || 0), 0);
        const fundReturnTotal = Number(reservation.fundReturnTotal || 0);
        const calculatedReservationReturnTotal = Number(reservation.calculatedReservationReturnTotal || reservation.proposedReservationReturnTotal || 0);
        const proposedReservationReturnTotal = Number(reservation.proposedReservationReturnTotal || 0);
        const reservationPersonReturnCap = Number(reservation.reservationPersonReturnCap || 0);
        const personReturnCapReductionAmount = Number(reservation.personReturnCapReductionAmount || 0);
        const wasPersonReturnCapped = Boolean(reservation.wasPersonReturnCapped || personReturnCapReductionAmount > 0.001);
        const totalRequiredReturn = proposedReservationReturnTotal;
        const executedReturns = Number(reservation.outgoingTransfers || 0);
        const returnDifference = totalRequiredReturn - executedReturns;

        return {
          ...reservation,
          participants,
          participantsCount: participants.length,
          equalShareTotal,
          participantAdjustmentsTotal,
          manualAdjustmentsImpactTotal,
          cancelledTfgReturnsTotal,
          unusedNnwReturnsTotal,
          unusedKrReturnsTotal,
          unusedInsuranceReturnsTotal,
          fullParticipantRefundsTotal,
          fundReturnTotal,
          calculatedReservationReturnTotal,
          proposedReservationReturnTotal,
          reservationPersonReturnCap,
          personReturnCapReductionAmount,
          wasPersonReturnCapped,
          totalRequiredReturn,
          executedReturns,
          returnDifference,
          hasFullParticipantRefunds: participants.some((p) => p.effectiveFullRefundAmount > 0),
          hasExcludedNnwInParticipantFullRefunds: participants.some((p) => p.hasExcludedNnwInFullRefund),
          hasExcludedNnwInReservationFullRefund: reservation.hasFullReservationRefund && participants.some((p) => p.hasExcludedNnwInFullRefund),
          hasManualAdjustments: manualAdjustmentsImpactTotal > 0,
          hasCancelledTfgReturns: cancelledTfgReturnsTotal > 0,
          hasUnusedNnwReturns: unusedNnwReturnsTotal > 0,
          hasUnusedKrReturns: unusedKrReturnsTotal > 0,
          hasUnusedInsuranceReturns: unusedInsuranceReturnsTotal > 0,
          hasEqualShare: equalShareTotal > 0,
          hasFundReturn: fundReturnTotal > 0,
        };
      })
      .filter((reservation) => reservation.proposedReservationReturnTotal > 0 || Number(reservation.fundReturnTotal || 0) > 0 || Number(reservation.outgoingTransfers || 0) > 0)
      .sort((a, b) => b.totalRequiredReturn - a.totalRequiredReturn);
  }, [variant.reservations]);

  const eligibleStatusCounts = useMemo(() => {
    const counts = { active: 0, present: 0, absent: 0, cancelled: 0, excluded: 0, costPresent: 0, costAbsent: 0, costCancelled: 0, administrativeExceptions: 0, administrativeExceptionNames: [] };

    (variant.reservations ?? []).forEach((reservation) => {
      if (reservation.hasFullReservationRefund) return;

      (reservation.participants ?? []).forEach((participant) => {
        const effectiveFullRefundAmount = Number(participant.fullRefundAmount || 0);
        if (effectiveFullRefundAmount > 0) return;

        const normalizedStatus = String(participant.status ?? "").toLowerCase();
        const isPresent = normalizedStatus === "present" || normalizedStatus === "2";
        const isAbsent = normalizedStatus === "absent" || normalizedStatus === "3";
        const isCancelled = normalizedStatus === "cancelled" || normalizedStatus === "4";
        const isEligibleStatus = isPresent || isAbsent || isCancelled;
        const isExcluded = participant.isExcludedFromAutomaticSettlement === true;

        if (isPresent) counts.costPresent += 1;
        else if (isAbsent) counts.costAbsent += 1;
        else if (isCancelled) counts.costCancelled += 1;

        if ((participant.isAdministrativeCostExempt || isExcluded) && isEligibleStatus) {
          counts.administrativeExceptions += 1;
          counts.administrativeExceptionNames.push(participant.participantName || "—");
        }

        if (isExcluded) {
          counts.excluded += 1;
          return;
        }

        if (isPresent) counts.present += 1;
        else if (isAbsent) counts.absent += 1;
        else if (isCancelled) counts.cancelled += 1;
        else counts.active += 1;
      });
    });

    return counts;
  }, [variant.reservations]);

  const explanationData = useMemo(() => getSettlementLedgerDescriptions({ rows, formatCurrency, administrativeCostPerPerson: 6, tfgCostPerPerson: 4, eligibleCounts: eligibleStatusCounts }), [rows, eligibleStatusCounts]);

  const ledgerBaseRows = [
    { label: "Dofinansowania z funduszu", value: variant.subsidiesCost, type: "plus", infoKey: "subsidiesCost" },
    { label: "Koszty administracyjne", value: variant.administrativeCosts, type: "minus", infoKey: "administrativeCosts" },
    { label: "Turystyczny Fundusz Gwarancyjny", value: variant.retainedTfgCosts, type: "minus", infoKey: "retainedTfgCosts" },
    { label: "NNW", value: variant.nnwCosts, type: "minus", infoKey: "nnwCosts" },
    { label: "Koszty wariantu", value: variant.variantExpensesTotal, type: "minus", infoKey: "variantExpensesTotal" },
  ].filter((x) => Number(x.value || 0) !== 0);

  const toggleInfoRow = (infoKey) => setSelectedInfoKey((prev) => (prev === infoKey ? null : infoKey));

  const renderStatusSummaryFormula = (formula) => {
    if (!formula) return null;
    const badges = [];

    if (formula.showPresent) badges.push(<div key="present" className="settlement-ledger-status-pill settlement-ledger-status-pill--present"><i className="fa-solid fa-user-plus" /><span>{formula.present}</span></div>);
    if (formula.showAbsent) badges.push(<div key="absent" className="settlement-ledger-status-pill settlement-ledger-status-pill--absent"><i className="fa-solid fa-user-minus" /><span>{formula.absent}</span></div>);
    if (formula.showCancelled) badges.push(<div key="cancelled" className="settlement-ledger-status-pill settlement-ledger-status-pill--cancelled"><i className="fa-solid fa-user-xmark" /><span>{formula.cancelled}</span></div>);

    return (
      <div className="settlement-inline-formula">
        <span className="settlement-inline-formula-operator">(</span>
        {badges.map((badge, idx) => <React.Fragment key={idx}>{idx > 0 && <span className="settlement-inline-formula-operator">+</span>}{badge}</React.Fragment>)}
        {Number(formula.administrativeExceptions || 0) > 0 && (
          <>
            <span className="settlement-inline-formula-operator">−</span>
            <div className="settlement-ledger-status-pill settlement-ledger-status-pill--active" title={`Bez kosztu administracyjnego: ${(formula.administrativeExceptionNames || []).join(", ")}`}>
              <i className="fa-solid fa-receipt" />
              <span>{formula.administrativeExceptions}</span>
            </div>
          </>
        )}
        <span className="settlement-inline-formula-operator">)</span>
        <span className="settlement-inline-formula-operator">×</span>
        <strong>{formula.multiplierLabel}</strong>
        <span className="settlement-inline-formula-operator">=</span>
        <strong>{formula.resultLabel}</strong>
      </div>
    );
  };

  const renderInlineExplanation = (infoKey) => {
    if (selectedInfoKey !== infoKey) return null;
    const data = explanationData?.[infoKey];
    if (!data) return null;

    return (
      <div className="settlement-ledger-inline-explanation">
        <div className="settlement-ledger-inline-explanation-text">{data.description}</div>
        {renderStatusSummaryFormula(data.formula)}
        {data.badges?.length > 0 && (
          <div className="settlement-ledger-inline-badges">
            {data.badges.map((badge, idx) => {
              let className = "settlement-mini-chip";
              let icon = null;
              if (badge.type === "refund-danger") { className = "settlement-chip settlement-chip-danger"; icon = <i className="fa-solid fa-rotate-left" />; }
              else if (badge.type === "refund") { className = "settlement-mini-chip settlement-mini-chip-refund"; icon = <i className="fa-solid fa-rotate-left" />; }
              else if (badge.type === "tfg") { className = "settlement-mini-chip settlement-mini-chip-tfg"; icon = <i className="fa-solid fa-shield" />; }
              else if (badge.type === "insurance") { className = "settlement-mini-chip settlement-mini-chip-refund"; icon = <i className="fa-solid fa-shield-halved" />; }
              else if (badge.type === "adjustment") { className = "settlement-mini-chip settlement-mini-chip-adjustment"; icon = <i className="fa-solid fa-sliders" />; }
              else if (badge.type === "share") { className = "settlement-mini-chip settlement-mini-chip-share"; icon = <i className="fa-solid fa-people-group" />; }
              return <span key={`${badge.label}-${idx}`} className={className}>{icon}{badge.label}</span>;
            })}
          </div>
        )}
        {data.items?.length > 0 && (
          <div className="settlement-ledger-inline-explanation-list">
            {data.items.map((item, idx) => (
              <div key={`${item.label}-${idx}`} className="settlement-ledger-inline-explanation-item">
                <div className="settlement-ledger-inline-explanation-item-left">
                  {item.badgeType === "refund" && <span className="settlement-mini-chip settlement-mini-chip-refund"><i className="fa-solid fa-rotate-left" />pełny zwrot</span>}
                  {item.badgeType === "tfg" && <span className="settlement-mini-chip settlement-mini-chip-tfg"><i className="fa-solid fa-shield" />TFG</span>}
                  {item.badgeType === "insurance" && <span className="settlement-mini-chip settlement-mini-chip-refund"><i className="fa-solid fa-shield-halved" />NNW / KR</span>}
                  <div className="settlement-ledger-inline-explanation-item-text"><span>{item.label}</span>{item.meta && <small>{item.meta}</small>}</div>
                </div>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settlement-variant-card">
      <div className="settlement-variant-card-header"><div><h4>{variant.variantName}</h4>{variant.isNegativeBalance && <div className="settlement-variant-warning">{variant.warningMessage}</div>}</div></div>

      <div className="settlement-ledger-summary">
        <div className="settlement-ledger-col">
          <div className="settlement-ledger-title">Rozliczenie wariantu</div>

          <div className="settlement-ledger-group">
            <div className={`settlement-ledger-row settlement-ledger-row--income settlement-ledger-row--clickable ${selectedInfoKey === "incomingTransfers" ? "is-active" : ""}`} onClick={() => toggleInfoRow("incomingTransfers")}>
              <span>Wpłaty <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
              <strong>{formatCurrency(variant.incomingTransfers)}</strong>
            </div>
            {renderInlineExplanation("incomingTransfers")}
          </div>

          {ledgerBaseRows.map((row) => (
            <div key={row.label} className="settlement-ledger-group">
              <div className={`settlement-ledger-row settlement-ledger-row--clickable ${row.type === "plus" ? "settlement-ledger-row--adjustment" : ""} ${selectedInfoKey === row.infoKey ? "is-active" : ""}`} onClick={() => toggleInfoRow(row.infoKey)}>
                <span>{row.label} <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
                <strong>{row.type === "plus" ? "+ " : "- "}{formatCurrency(Math.abs(Number(row.value || 0)))}</strong>
              </div>
              {renderInlineExplanation(row.infoKey)}
            </div>
          ))}

          <div className="settlement-ledger-divider" />
          <div className="settlement-ledger-row settlement-ledger-row--result"><span>Pula przed zwrotami</span><strong className="settlement-ledger-value--pool">{formatCurrency(variant.remainingPoolBeforeDistribution)}</strong></div>

          <div className="settlement-ledger-group">
            <div className={`settlement-ledger-row settlement-ledger-row--result settlement-ledger-row--clickable ${selectedInfoKey === "totalManualReturns" ? "is-active" : ""}`} onClick={() => toggleInfoRow("totalManualReturns")}>
              <span>Zwroty indywidualne <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
              <strong className="settlement-ledger-value--returns">- {formatCurrency(variant.totalManualReturns)}</strong>
            </div>
            {renderInlineExplanation("totalManualReturns")}
          </div>

          {[['totalFullRefunds','- w tym pełne zwroty',variant.totalFullRefunds],['cancelledTfgReturns','- odzyskane TFG',variant.cancelledTfgReturns],['unusedInsuranceReturns','- niewykupione NNW / KR',variant.unusedInsuranceReturns],['totalManualAdjustmentsImpact','- wpływ ręcznych korekt',variant.totalManualAdjustmentsImpact]].filter(([, , value]) => Number(value || 0) !== 0).map(([key,label,value]) => (
            <div className="settlement-ledger-group" key={key}>
              <div className={`settlement-ledger-row settlement-ledger-row--sub settlement-ledger-row--clickable ${selectedInfoKey === key ? "is-active" : ""}`} onClick={() => toggleInfoRow(key)}>
                <span>{label} <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
                <strong>({formatCurrency(value)})</strong>
              </div>
              {renderInlineExplanation(key)}
            </div>
          ))}

          <div className="settlement-ledger-divider" />
          <div className="settlement-ledger-group">
            <div className={`settlement-ledger-row settlement-ledger-row--result settlement-ledger-row--clickable ${selectedInfoKey === "poolAvailableForEqualShare" ? "is-active" : ""}`} onClick={() => toggleInfoRow("poolAvailableForEqualShare")}>
              <span>Pula do podziału między os. ucz. <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
              <strong className="settlement-ledger-value--pool">{formatCurrency(variant.poolAvailableForEqualShare)}</strong>
            </div>
            {renderInlineExplanation("poolAvailableForEqualShare")}
          </div>

          <div className="settlement-ledger-group">
            <div className={`settlement-ledger-row settlement-ledger-row--sub settlement-ledger-row--clickable ${selectedInfoKey === "totalEqualShareDistributed" ? "is-active" : ""}`} onClick={() => toggleInfoRow("totalEqualShareDistributed")}>
              <span>- suma z proponowanych zwrotów po podziale <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span>
              <strong>({formatCurrency(variant.totalEqualShareDistributed)})</strong>
            </div>
            {renderInlineExplanation("totalEqualShareDistributed")}
          </div>

          {Number(variant.totalReservationCapReductions || 0) > 0 && (
            <div className="settlement-ledger-group">
              <div className="settlement-ledger-row settlement-ledger-row--adjustment"><span>Ograniczenie do wpłat rezerwacji <i className="fa-solid fa-circle-info settlement-ledger-info-icon" /></span><strong>+ {formatCurrency(variant.totalReservationCapReductions)}</strong></div>
              <div className="settlement-ledger-inline-explanation"><div className="settlement-ledger-inline-explanation-text">W przypadku rezerwacji bez pełnego zwrotu całej rezerwacji końcowa kwota „Do osób” została ograniczona do sumy wpłat rezerwacji, pomniejszonej o NNW, jeśli było wykupione.</div></div>
            </div>
          )}

          <div className="settlement-ledger-divider" />
          <div className="settlement-ledger-row settlement-ledger-row--result"><span>Saldo po rozliczeniu</span><strong className={Number(variant.variantBalanceAfterProposedReturns || 0) < 0 ? "settlement-balance-negative" : "settlement-balance-positive"}>{formatCurrency(variant.variantBalanceAfterProposedReturns)}</strong></div>
        </div>

        <div className="settlement-ledger-side">
          <div className="settlement-ledger-side-card settlement-ledger-side-card--neutral">
            <span>Osoby uwzględnione w rozliczeniu</span>
            <div className="settlement-ledger-status-summary">
              <div className="settlement-ledger-status-pill settlement-ledger-status-pill--present"><i className="fa-solid fa-user-plus" /><span>{eligibleStatusCounts.present}</span></div>
              <div className="settlement-ledger-status-pill settlement-ledger-status-pill--absent"><i className="fa-solid fa-user-minus" /><span>{eligibleStatusCounts.absent}</span></div>
              <div className="settlement-ledger-status-pill settlement-ledger-status-pill--cancelled"><i className="fa-solid fa-user-xmark" /><span>{eligibleStatusCounts.cancelled}</span></div>
              {eligibleStatusCounts.active > 0 && <div className="settlement-ledger-status-pill settlement-ledger-status-pill--active"><i className="fa-solid fa-user" /><span>{eligibleStatusCounts.active}</span></div>}
            </div>
            <div className="settlement-ledger-status-note"><i className="fa-solid fa-circle-info" /><span>Nie uwzględniamy tutaj osób z rezerwacji z pełnym zwrotem, osób z pełnym zwrotem indywidualnym oraz osób wyłączonych z automatycznego rozliczenia.</span></div>
            {eligibleStatusCounts.excluded > 0 && <small>Poza automatycznym rozliczeniem: {eligibleStatusCounts.excluded}</small>}
            <small>Na osobę z puli do podziału: {formatCurrency(variant.equalSharePerParticipant)}</small>
          </div>

          <div className="settlement-ledger-side-card settlement-ledger-side-card--primary"><span>Planowane zwroty łącznie</span><strong>{formatCurrency(variant.totalProposedReturns)}</strong><small>Końcowa kwota „Do osoby” z tabeli poniżej.</small></div>

          <div className="settlement-ledger-side-card settlement-ledger-side-card--fund">
            <div className="settlement-fund-card-header">
              <span>Zwrócić do funduszu</span>
              <button type="button" className={`settlement-info-icon-button ${showFundInfo ? "is-active" : ""}`} onClick={() => setShowFundInfo((prev) => !prev)} title="Wyjaśnienie zasad zwrotu do funduszu"><i className="fa-solid fa-circle-info" /></button>
            </div>
            <strong className="settlement-ledger-value--fund">{formatCurrency(variant.fundReturnsTotal)}</strong>
            <small>Kwota odzyskiwana z planowanych zwrotów.</small>
            {showFundInfo && (
              <div className="settlement-fund-card-explanation">
                <div className="settlement-fund-card-explanation-item"><i className="fa-solid fa-rotate-left" /><span>Jeśli ustawiono pełny zwrot na poziomie całej rezerwacji, to do osób wraca łączna suma wpłat przypisana do rezerwacji, a do funduszu wraca suma dofinansowań osób z tej rezerwacji.</span></div>
                <div className="settlement-fund-card-explanation-item"><i className="fa-solid fa-user-check" /><span>Jeśli os. ucz. mająca dofinansowanie była obecna na wyjeździe, to ewentualny zwrot w pierwszej kolejności trafia do tej osoby. Dopiero nadwyżka ponad wykonane przez nią wpłaty wraca do Funduszu.</span></div>
                <div className="settlement-fund-card-explanation-item"><i className="fa-solid fa-user-xmark" /><span>Jeśli os. ucz. mająca dofinansowanie nie była obecna lub była anulowana i nikt nie wskoczył na jej miejsce, to ewentualny zwrot w pierwszej kolejności zasila Fundusz, aby odzyskać przyznane wcześniej dofinansowanie. Dopiero nadwyżka ponad kwotę dofinansowania wraca do osoby.</span></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="settlement-empty-box">Brak rezerwacji z proponowanym zwrotem w tym wariancie.</div>
      ) : (
        <div className="variant-table-wrapper">
          <table className="variant-reservation-table">
            <thead><tr><th>Rezerwacja</th><th>Składowe zwrotu</th><th>Do funduszu</th><th>Do osób</th><th>Wyk. do os. zwr.</th><th>Różnica</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const isExpanded = expandedRow === r.reservationGuid;
                return (
                  <React.Fragment key={r.reservationGuid}>
                    <tr className={`clickable ${isExpanded ? "expanded-row" : ""}`} onClick={() => setExpandedRow(isExpanded ? null : r.reservationGuid)}>
                      <td><span className={`reg-code status-${r.status}`}>{r.reservationCode}</span><span className="primary-participant-name">{r.primaryParticipantName}</span></td>
                      <td>
                        <div className="settlement-type-cell">
                          {r.hasFullReservationRefund && <span className="settlement-chip settlement-chip-danger"><i className="fa-solid fa-rotate-left" />{r.hasExcludedNnwInReservationFullRefund ? "Pełny zwrot rezerwacji (bez NNW)" : "Pełny zwrot rezerwacji"}</span>}
                          <div className="settlement-type-tags">
                            {!r.hasFullReservationRefund && r.hasFullParticipantRefunds && <span className="settlement-mini-chip settlement-mini-chip-refund"><i className="fa-solid fa-rotate-left" />{r.hasExcludedNnwInParticipantFullRefunds ? "Pełny zwrot osoby (bez NNW)" : "Pełny zwrot osoby"}</span>}
                            {r.hasManualAdjustments && <span className="settlement-mini-chip settlement-mini-chip-adjustment"><i className="fa-solid fa-sliders" />korekta</span>}
                            {r.hasCancelledTfgReturns && <span className="settlement-mini-chip settlement-mini-chip-tfg"><i className="fa-solid fa-shield" />TFG</span>}
                            {r.hasUnusedNnwReturns && <span className="settlement-mini-chip settlement-mini-chip-refund"><i className="fa-solid fa-shield-halved" />NNW</span>}
                            {r.hasUnusedKrReturns && <span className="settlement-mini-chip settlement-mini-chip-refund"><i className="fa-solid fa-shield-halved" />KR</span>}
                            {r.hasEqualShare && <span className="settlement-mini-chip settlement-mini-chip-share"><i className="fa-solid fa-people-group" />podział</span>}
                            {r.hasFundReturn && <span className="settlement-mini-chip settlement-mini-chip-fund"><i className="fa-solid fa-piggy-bank" />fundusz</span>}
                            {r.wasPersonReturnCapped && <span className="settlement-mini-chip settlement-mini-chip-warning"><i className="fa-solid fa-arrow-down-short-wide" />Redukcja</span>}
                          </div>
                        </div>
                      </td>
                      <td><strong>{formatCurrency(r.fundReturnTotal)}</strong></td>
                      <td><strong>{formatCurrency(r.proposedReservationReturnTotal)}</strong></td>
                      <td>{formatCurrency(r.executedReturns)}</td>
                      <td><strong className={r.returnDifference > 0 ? "balance-positive" : r.returnDifference < 0 ? "balance-negative" : "balance-zero"}>{formatCurrency(r.returnDifference)}</strong></td>
                    </tr>

                    {isExpanded && (
                      <tr className="expanded-participants-row">
                        <td colSpan={6}>
                          {r.hasFullReservationRefund ? (
                            <div className="reservation-full-refund-info-box">
                              <div className="reservation-full-refund-info-title"><i className="fa-solid fa-rotate-left" /><span>Pełny zwrot na poziomie całej rezerwacji</span></div>
                              <div className="reservation-full-refund-info-text">Dla tej rezerwacji zwracana jest łączna suma wpłat przypisana do rezerwacji.</div>
                              {r.hasExcludedNnwInReservationFullRefund && (
                                <div className="reservation-full-refund-extra-note"><i className="fa-solid fa-shield-halved" /><span>Zwrot został pomniejszony o koszt wykupienia NNW dla: {r.participants.filter((p) => p.hasExcludedNnwInFullRefund).map((p) => p.participantName).filter(Boolean).join(", ")} ({formatCurrency(r.participants.filter((p) => p.hasExcludedNnwInFullRefund).reduce((sum, p) => sum + Number(p.excludedNnwAmount || 0), 0))}).</span></div>
                              )}
                            </div>
                          ) : (
                            <div className="reservation-participant-details">
                              {r.wasPersonReturnCapped && <div className="reservation-full-refund-extra-note"><i className="fa-solid fa-circle-info" /><span>Końcowa kwota <strong>„Do osób”</strong> w głównym wierszu została ograniczona do sumy wpłat rezerwacji{Number(r.reservationPersonReturnCap || 0) > 0 ? ` (${formatCurrency(r.reservationPersonReturnCap)})` : ""}. Szczegóły per osoba poniżej pokazują surowe wyliczenie przed tym ograniczeniem. Obcięto: <strong>{formatCurrency(r.personReturnCapReductionAmount)}</strong>.</span></div>}
                              <div className="participant-table-wrapper">
                                <table className="participant-table">
                                  <thead><tr><th>Osoba</th><th>Pełny zwrot</th><th>Podział</th><th>Zwrot ind.</th><th>TFG</th><th>NNW / KR</th><th>Do funduszu</th><th>Do osoby</th><th>Komentarz</th></tr></thead>
                                  <tbody>
                                    {r.participants.map((p) => {
                                      const status = getParticipantStatusStyle(p.status);
                                      const disabled = p.isCoveredByFullRefund;
                                      const unusedNnw = Number(p.unusedNnwReturnAmount || 0);
                                      const unusedKr = Number(p.unusedKrReturnAmount || 0);
                                      return (
                                        <tr key={p.participantGuid}>
                                          <td>
                                            <div className="participant-cell-main">
                                              <span className={`participant-name-icon ${status.statusClass}`}><i className={`fa-solid ${status.icon}`} /></span>
                                              <div className="participant-cell-text"><span className={status.strikeThrough ? "strike-through" : ""}>{p.participantName}</span>{p.isAdministrativeCostExempt && <small>bez kosztu admin.</small>}{p.isExcludedFromAutomaticSettlement && <small>poza auto. rozliczeniem</small>}</div>
                                            </div>
                                          </td>
                                          <td><strong>{formatCurrency(p.effectiveFullRefundAmount)}</strong></td>
                                          <td className={disabled ? "settlement-cell-disabled" : ""}>{disabled ? "—" : formatCurrency(p.equalShareAmount)}</td>
                                          <td className={disabled ? "settlement-cell-disabled" : ""}>{disabled ? "—" : formatCurrency(Math.abs(p.participantAdjustmentAmount ?? 0))}</td>
                                          <td className={disabled ? "settlement-cell-disabled" : ""}>{disabled ? "—" : formatCurrency(p.cancelledTfgReturnAmount)}</td>
                                          <td className={disabled ? "settlement-cell-disabled" : ""}>{disabled ? "—" : formatCurrency(unusedNnw + unusedKr)}</td>
                                          <td>{formatCurrency(p.fundReturnAmount)}</td>
                                          <td><strong>{formatCurrency(p.proposedReturnAmount)}</strong></td>
                                          <td>{p.comment || "—"}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SettlementVariantReturnsCard;